import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { Loader2, Paperclip, Search, Sparkles, X } from 'lucide-react'
import {
  fetchJurisdictions, fetchLegalActs, searchLegalActs, LegalAct, Jurisdiction, LegalStatusValue,
} from '../../lib/api/api'
import { useAttachments } from '../../contexts/AttachmentContext'
import { isCantonCode } from '../../shared/cantons'
import LegalStatusBadge from '../upload/components/LegalStatusBadge'
import { formatDate, formatNumber } from '../../shared/utils/format'
import LegalActDetail from './LegalActDetail'

const PAGE_SIZE = 20

type StatusFilter = 'all' | LegalStatusValue

type Tab = 'CH' | 'cantonal' | 'INT'

// One tab per kind of law: federal acts, cantonal acts (one canton at a time), international treaties
const TABS: ReadonlyArray<{ tab: Tab; label: string }> = [
  { tab: 'CH', label: 'legalActs.tabs.federal' },
  { tab: 'cantonal', label: 'legalActs.tabs.cantonal' },
  { tab: 'INT', label: 'legalActs.tabs.treaties' },
]

const parseJurisdiction = (value: string | null): Jurisdiction =>
  value === 'CH' || value === 'INT' || isCantonCode(value) ? (value as Jurisdiction) : 'CH'

const tabOf = (jurisdiction: Jurisdiction): Tab =>
  jurisdiction === 'CH' || jurisdiction === 'INT' ? jurisdiction : 'cantonal'

export default function LegalActsPage() {
  const { t } = useTranslation()
  const { attachDocument, detachDocument, isDocumentAttached } = useAttachments()
  const [searchParams, setSearchParams] = useSearchParams()
  const jurisdiction: Jurisdiction = parseJurisdiction(searchParams.get('jurisdiction'))

  const [items, setItems] = useState<LegalAct[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [textQuery, setTextQuery] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedActId, setSelectedActId] = useState<number | null>(null)

  const [cantonOptions, setCantonOptions] = useState<string[]>(['TI'])

  const isCantonal = tabOf(jurisdiction) === 'cantonal'

  useEffect(() => {
    if (!isCantonal) return
    let active = true
    fetchJurisdictions()
      .then((res) => {
        if (!active) return
        const codes = res.data.cantons.filter((c) => c.available).map((c) => c.code)
        if (codes.length) setCantonOptions(codes)
      })
      .catch(() => { /* Ticino only */ })
    return () => { active = false }
  }, [isCantonal])

  const [semanticQuery, setSemanticQuery] = useState('')
  const [semanticResults, setSemanticResults] = useState<Array<Record<string, any>> | null>(null)
  const [isSearching, setIsSearching] = useState(false)

  useEffect(() => {
    let active = true
    setIsLoading(true)
    setError(null)
    fetchLegalActs({
      jurisdiction,
      status: statusFilter === 'all' ? undefined : statusFilter,
      q: textQuery || undefined,
      limit: PAGE_SIZE,
      offset,
    })
      .then((res) => {
        if (!active) return
        setItems(res.data.items)
        setTotal(res.data.total)
      })
      .catch(() => {
        if (active) setError(t('legalActs.errors.loadFailed'))
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })
    return () => { active = false }
  }, [jurisdiction, statusFilter, textQuery, offset, t])

  const setJurisdiction = (next: Jurisdiction) => {
    if (next === jurisdiction) return
    const params = new URLSearchParams(searchParams)
    params.set('jurisdiction', next)
    setSearchParams(params)
    setOffset(0)
    setSelectedActId(null)
  }

  const handleStatusChange = (value: StatusFilter) => {
    setStatusFilter(value)
    setOffset(0)
  }

  const handleTextQueryChange = (value: string) => {
    setTextQuery(value)
    setOffset(0)
  }

  const runSemanticSearch = async () => {
    if (!semanticQuery.trim()) return
    setIsSearching(true)
    setError(null)
    try {
      const res = await searchLegalActs({ q: semanticQuery, jurisdiction, mode: 'semantic' })
      setSemanticResults(res.data)
    } catch {
      setError(t('legalActs.errors.loadFailed'))
    } finally {
      setIsSearching(false)
    }
  }

  const clearSemanticSearch = () => {
    setSemanticQuery('')
    setSemanticResults(null)
  }

  const handleAttachToggle = (act: LegalAct) => {
    const id = String(act.id)
    if (isDocumentAttached(id, 'legal_act')) {
      detachDocument(id, 'legal_act')
    } else {
      attachDocument({ id, title: act.title, type: 'legal_act', doc_type: 'legal_act', file_name: act.title })
    }
  }

  if (selectedActId !== null) {
    return (
      <LegalActDetail
        actId={selectedActId}
        onClose={() => setSelectedActId(null)}
        onDeleted={() => {
          setSelectedActId(null)
          setOffset(0)
        }}
      />
    )
  }

  const sourceLabel = (source: string | null): string => {
    if (source === 'fedlex') return t('legalActs.list.source.fedlex')
    if (source === 'rl') return t('legalActs.list.source.rl')
    if (source === 'lexwork') return t('legalActs.list.source.lexwork')
    return ''
  }

  return (
    <div className="space-y-6">
      <h1 className="font-display text-3xl font-bold text-gray-900 dark:text-gray-100">{t('nav.legalActs')}</h1>

      <div role="tablist" className="flex border-b border-gray-200 dark:border-gray-700">
        {TABS.map((tab) => (
          <button
            key={tab.tab}
            type="button"
            role="tab"
            aria-selected={tabOf(jurisdiction) === tab.tab}
            onClick={() => setJurisdiction(tab.tab === 'cantonal' ? (tabOf(jurisdiction) === 'cantonal' ? jurisdiction : 'TI') : tab.tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tabOf(jurisdiction) === tab.tab
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t(tab.label)}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={textQuery}
            onChange={(e) => handleTextQueryChange(e.target.value)}
            placeholder={t('legalActs.filters.textPlaceholder')}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
          />
        </div>
        {tabOf(jurisdiction) === 'cantonal' && (
          <select
            aria-label={t('legalActs.filters.canton')}
            value={jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value as Jurisdiction)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
          >
            {(cantonOptions.includes(jurisdiction) ? cantonOptions : [jurisdiction, ...cantonOptions]).map((code) => (
              <option key={code} value={code}>{t(`cantons.${code}`)}</option>
            ))}
          </select>
        )}
        <select
          aria-label={t('legalActs.filters.status')}
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value as StatusFilter)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
        >
          <option value="all">{t('common.all')}</option>
          <option value="in_vigore">{t('legalStatus.in_vigore')}</option>
          <option value="abrogato">{t('legalStatus.abrogato')}</option>
        </select>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={semanticQuery}
          onChange={(e) => setSemanticQuery(e.target.value)}
          placeholder={t('legalActs.filters.semanticPlaceholder')}
          onKeyDown={(e) => { if (e.key === 'Enter') runSemanticSearch() }}
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
        />
        <button
          type="button"
          onClick={runSemanticSearch}
          disabled={isSearching}
          className="flex items-center gap-1 px-3 py-2 rounded-lg bg-purple-600 text-white text-sm disabled:opacity-50"
        >
          <Sparkles size={16} />
          {t('common.semanticSearch')}
        </button>
        {semanticResults && (
          <button type="button" onClick={clearSemanticSearch} className="text-sm text-gray-500 hover:text-gray-700">
            <X size={16} />
          </button>
        )}
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {semanticResults ? (
        <div className="space-y-2">
          <h2 className="text-sm font-medium text-gray-700">{t('legalActs.semanticResults.title')}</h2>
          {semanticResults.length === 0 ? (
            <p className="text-gray-500 text-sm">{t('legalActs.semanticResults.empty')}</p>
          ) : (
            <ul className="space-y-2">
              {semanticResults.map((hit, index) => {
                const label = hit.label ?? hit.article_number
                const rubric = hit.rubric ?? hit.subtitle
                const title = hit.title ?? hit.doc_title
                const snippet = hit.snippet ?? hit.content
                const actIdForHit = hit.act_id
                return (
                  <li key={`${actIdForHit ?? index}-${hit.article_id ?? index}`} className="p-3 border rounded-md bg-white dark:bg-gray-800">
                    <button
                      type="button"
                      onClick={() => actIdForHit != null && setSelectedActId(Number(actIdForHit))}
                      className="text-left w-full"
                    >
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        <span>{title} {label ? `– ${label}` : ''}</span>
                        {hit.language && hit.language !== 'it' && (
                          <span className="text-xs font-medium border rounded-full px-2 py-0.5 bg-blue-50 text-blue-700 border-blue-200">
                            {t('legalActs.languageBadge.' + hit.language)}
                          </span>
                        )}
                      </div>
                      {rubric && <div className="text-sm text-gray-600">{rubric}</div>}
                      {snippet && <div className="text-sm text-gray-500 mt-1">{String(snippet).slice(0, 240)}</div>}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      ) : (
        <>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-gray-400" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t('legalActs.list.noResults')}</p>
          ) : (
            <div className="space-y-2">
              {items.map((act) => {
                const attached = isDocumentAttached(String(act.id), 'legal_act')
                const otherLanguages = (act.languages || []).filter((l) => l !== act.language)
                return (
                  <div
                    key={act.id}
                    onClick={() => setSelectedActId(act.id)}
                    className="p-4 border rounded-lg bg-white dark:bg-gray-800 hover:shadow-md transition-shadow cursor-pointer flex items-start justify-between gap-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-gray-500">{act.number}</span>
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">{act.title}</h3>
                        {act.short_title && <span className="text-sm text-gray-500">({act.short_title})</span>}
                        <LegalStatusBadge
                          legal_status={act.legal_status}
                          legal_status_source={act.legal_status_source}
                          legal_status_reference={act.legal_status_reference}
                          legal_status_url={act.legal_status_url}
                          legal_status_checked_at={act.legal_status_checked_at}
                          text_version_date={act.text_version_date}
                        />
                        {act.language && act.language !== 'it' && (
                          <span className="text-xs font-medium border rounded-full px-2 py-0.5 bg-blue-50 text-blue-700 border-blue-200">
                            {t('legalActs.languageBadge.' + act.language)}
                          </span>
                        )}
                        {otherLanguages.length > 0 && (
                          <span className="text-xs text-gray-500">
                            {t('legalActs.alsoIn', { list: otherLanguages.map((l) => t('legalActs.languageBadge.' + l)).join(', ') })}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                        {act.text_version_date && <span>{t('legalActs.list.textVersionDate', { date: formatDate(act.text_version_date) })}</span>}
                        <span>{t('legalActs.list.articlesCount', { n: formatNumber(act.articles_count) })}</span>
                        {sourceLabel(act.official_source) && <span>{sourceLabel(act.official_source)}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleAttachToggle(act) }}
                      title={attached ? t('common.detach') : t('common.attach')}
                      aria-label={attached ? t('common.detach') : t('common.attach')}
                      className={`p-2 rounded-full flex-shrink-0 ${
                        attached ? 'text-primary-600 bg-primary-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Paperclip size={18} />
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          {total > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                className="px-3 py-1 border rounded-md disabled:opacity-50"
              >
                {t('common.previous')}
              </button>
              <span className="text-sm text-gray-600">
                {t('common.showingRange', {
                  from: formatNumber(offset + 1),
                  to: formatNumber(Math.min(offset + PAGE_SIZE, total)),
                  total: formatNumber(total),
                })}
              </span>
              <button
                type="button"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= total}
                className="px-3 py-1 border rounded-md disabled:opacity-50"
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
